import React from "react"
import type { ReactNode } from "react"
import cx from "classnames"
import { DialogOverlay, DialogContent } from "@reach/dialog"
import { OldIconButton } from "../old-button"

export type DrawerProps = {
  /** Content rendered inside the drawer body */
  children?: ReactNode
  /** Accessible label for the drawer dialog */
  label?: string
  /** Heading displayed at the top of the drawer. Strings are styled automatically; pass a ReactNode for custom headings. */
  heading?: ReactNode
  /** Called when the drawer is dismissed (close button, overlay click, or Escape) */
  onClose: () => void
}

/**
 * A slide-in panel that opens from the right side of the screen.
 * Built on top of @reach/dialog for accessible overlay behavior.
 */
function Drawer({ children, label, onClose, heading }: DrawerProps) {
  const defaultDrawerClass = `
    bg-white
    flex
    flex-col
    fixed
    font-sans
    h-full
    max-w-2xl
    p-0
    right-0
    rounded
    shadow
    top-0
    w-4/5
  `
  const headingClass = cx("flex", "justify-between", "items-center", "p-6", {
    "border-b": !!heading,
    "border-gray-200": !!heading
  })
  const drawerClass = cx(defaultDrawerClass)
  const headingTitleClass = `
    text-gray-800
    text-2xl
    font-semibold
  `

  return (
    <DialogOverlay onDismiss={onClose}>
      <DialogContent
        key="modal-content"
        aria-label={label}
        className={drawerClass}
        style={{
          marginTop: 0,
          animation: "0.5s ease-in-out 0s 1 slide-in-from-right"
        }}
      >
        <header className={headingClass}>
          {typeof heading === "string" ? (
            <p className={headingTitleClass}>{heading}</p>
          ) : (
            heading
          )}

          <OldIconButton
            icon="close"
            is="plain"
            label="Close drawer"
            onClick={onClose}
          />
        </header>

        <div className="p-6 overflow-y-auto h-full flex-1">{children}</div>
      </DialogContent>
    </DialogOverlay>
  )
}

export { Drawer }
export default Drawer
